<template>
  <div class="compare-stats">
    <div class="stats-header">
      <h3><i class="bi bi-bar-chart-line"></i> Сравнение документов</h3>
      <div class="totals">
        <div class="total-card">
          <span class="label">Total совпадений</span>
          <span class="value" :class="matchClass(totalStats.matchPercent)">
            {{ totalStats.matchPercent }}%
          </span>
        </div>
      </div>
    </div>

    <!-- Сводная таблица по блокам -->
    <div class="summary-table">
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>Показатель</th>
          <th>Документ</th>
          <th>Размещённый текст</th>
          <th>Δ</th>
          <th>Совпадение</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in summaryRows" :key="row.key">
          <td class="num">{{ row.index }}</td>
          <td class="label">{{ row.label }}</td>
          <td class="num">{{ row.a }}</td>
          <td class="num">{{ row.b }}</td>
          <td class="num" :class="row.deltaClass">{{ row.delta }}</td>
          <td class="match">
            <span class="badge" :class="row.matchClass">{{ row.matchPercent }}%</span>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Детализация по параграфам -->
    <div class="paragraphs">
      <h4><i class="bi bi-file-text"></i> Детализация по блокам</h4>

      <div v-if="!paragraphStats.length" class="empty">
        <i class="bi bi-inbox"></i>
        <p>Нет данных для сравнения</p>
      </div>
      <div class="paragraph-card mb-0">
        <div class="paragraph-body">
          <div class="col column-header source-header">
            <div class="header-icon"><i class="bi bi-file-earmark-text"></i></div>
            <div class="header-text">
              <h4>Текстовый файл</h4>
              <small>Исходный документ</small>
            </div>
          </div>
          <div class="col column-header placement-header">
            <div class="header-icon"><i class="bi bi-layout-text-window-reverse"></i></div>
            <div class="header-text">
              <h4>Размещение</h4>
              <small>Текст на странице</small>
            </div>
          </div>
        </div>
      </div>

      <div
          v-for="(block, i) in paragraphStats"
          :key="'p-' + i"
          class="paragraph-card"
      >
        <div class="paragraph-head">
          <div class="col">
            <span class="index">Блок {{ i + 1 }}</span>
            <span class="h2" :title="block.h2a">
              <template v-if="i">{{ block.h2a || '—' }}</template>
              <template v-else>{{ doc.h1 }}</template>

          </span>
          </div>
          <div class="col">
          <span class="h2" :title="block.h2a || block.h2b">
            <template v-if="i">{{ block.h2b || '—' }}</template>
              <template v-else>{{ placed.h1 }}</template>
          </span>
            <span class="badge" :class="matchClass(block.matchPercent)">
            {{ block.matchPercent }}%
          </span>
          </div>
        </div>

        <div class="paragraph-body">
          <div class="col">
            <TargetTextOne :elems="block.elemsA" :text="doc" :block="doc.paragraphs[i]" :index="i"></TargetTextOne>

          </div>
          <div class="col">
            <TargetTextOne :elems="block.elemsB" :text="placed" :block="placed.paragraphs[i]"
                           :index="i"></TargetTextOne>

          </div>
        </div>

        <div class="paragraph-foot">
          <span class="chip">
            <i class="bi bi-image"></i> images: {{ block.imagesA }} / {{ block.imagesB }}
          </span>
          <span class="chip">
            <i class="bi bi-table"></i> tables: {{ block.tablesA }} / {{ block.tablesB }}
          </span>
          <span class="chip">
            <i class="bi bi-list-ul"></i> rows: {{ block.rowsA }} / {{ block.rowsB }}
          </span>
          <span class="chip">
            <i class="bi bi-link-45deg"></i> links: {{ block.linksA }} / {{ block.linksB }}
          </span>
          <span class="chip">
            <i class="bi bi-fonts"></i> chars: {{ block.charsA }} / {{ block.charsB }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TargetTextOne from "@/views/planfix/components/TargetTextOne.vue";

export default {
  name: 'CompareStats',
  components: {TargetTextOne},
  props: {
    doc: {
      type: Object,
      required: true
    },
    placed: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      summaryRows: [],
      totalStats: {}
    }
  },
  computed: {

    paragraphStats() {
      const docBlocks = this.sortBlocksByIndex(this.doc)
      const placedBlocks = this.sortBlocksByIndex(this.placed)

      const len = Math.max(docBlocks.length, placedBlocks.length)
      const result = []

      const total = {
        a: {h2: 0, images: 0, tables: 0, rows: 0, links: 0, chars: 0},
        b: {h2: 0, images: 0, tables: 0, rows: 0, links: 0, chars: 0},
      }

      for (let i = 0; i < len; i++) {
        const a = docBlocks[i] || null
        const b = placedBlocks[i] || null

        const statsA = this._extractBlockStats(a)
        const statsB = this._extractBlockStats(b)

        // Сравнение по 5 метрикам
        const metrics = [
          [statsA.images, statsB.images],
          [statsA.tables, statsB.tables],
          [statsA.rows, statsB.rows],
          [statsA.links, statsB.links],
          [statsA.chars, statsB.chars],
        ]

        const percents = metrics.map(([x, y]) => {
          const max = Math.max(x, y)
          return max === 0 ? 100 : Math.round((Math.min(x, y) / max) * 100)
        })

        const matchPercent = Math.round(
            percents.reduce((s, v) => s + v, 0) / percents.length
        )

        // Аккумулируем только то, что реально есть
        if (a) {
          total.a.h2 += 1
          total.a.images += statsA.images
          total.a.tables += statsA.tables
          total.a.rows += statsA.rows
          total.a.links += statsA.links
          total.a.chars += statsA.chars
        }
        if (b) {
          total.b.h2 += 1
          total.b.images += statsB.images
          total.b.tables += statsB.tables
          total.b.rows += statsB.rows
          total.b.links += statsB.links
          total.b.chars += statsB.chars
        }

        const isMatch = matchPercent >= 80

        result.push({
          h2a: a?.h2 || '',
          h2b: b?.h2 || '',
          elemsA: a?.elements || [],
          elemsB: b?.elements || [],
          imagesA: statsA.images,
          imagesB: statsB.images,
          tablesA: statsA.tables,
          tablesB: statsB.tables,
          rowsA: statsA.rows,
          rowsB: statsB.rows,
          linksA: statsA.links,
          linksB: statsB.links,
          charsA: statsA.chars,
          charsB: statsB.chars,
          matchPercent,
          isMatch,
        })
      }

      // Сводная таблица
      const defs = [
        {key: 'h2', label: 'H2 блоков'},
        {key: 'images', label: 'Изображений'},
        {key: 'tables', label: 'Таблиц'},
        {key: 'rows', label: 'Списков'},
        {key: 'links', label: 'Ссылок'},
        {key: 'chars', label: 'Знаков в текстах'},
      ]

      let totalPercentsSum = 0
      this.summaryRows = defs.map((d, i) => {
        const av = total.a[d.key]
        const bv = total.b[d.key]
        const max = Math.max(av, bv)
        const matchPercent = max === 0 ? 100 : Math.round((Math.min(av, bv) / max) * 100)
        const delta = bv - av

        totalPercentsSum += matchPercent

        return {
          index: i + 1,
          key: d.key,
          label: d.label,
          a: av,
          b: bv,
          delta: delta > 0 ? '+' + delta : String(delta),
          deltaClass: delta === 0 ? 'delta-zero' : 'delta-nonzero',
          matchPercent,
          matchClass: this.matchClass(matchPercent),
        }
      })

      const matchPercent = defs.length
          ? Math.round(totalPercentsSum / defs.length)
          : 0

      this.totalStats = {matchPercent, len}

      return result
    },

  },
  methods: {
    _extractBlockStats(block) {
    const stats = { images: 0, tables: 0, rows: 0, chars: 0 }

    if (!block) {
      return { ...stats, links: 0 }
    }

    const elems = Array.isArray(block.elements) ? block.elements : []

    for (let i = 0; i < elems.length; i++) {
      const e = elems[i]
      if (!e) continue

      switch (e.type) {
        case 'p':
          stats.chars += e.text?.length || 0
          break
        case 'rows': {
          const arr = Array.isArray(e.text) ? e.text : []
          stats.rows += arr.length
          for (let j = 0; j < arr.length; j++) {
            stats.chars += arr[j]?.length || 0
          }
          break
        }
        case 'image':
          stats.images++
          break
        case 'table':
          stats.tables++
          break
      }
    }

    stats.links = Array.isArray(block.links) ? block.links.length : 0
    return stats
  },
    sortBlocksByIndex(textDoc) {
      if (!textDoc || typeof textDoc !== "object") {
        return [];
      }

      const paragraphs = Array.isArray(textDoc.paragraphs) ? textDoc.paragraphs : [];
      const resultBlocks = [];

      for (const paragraph of paragraphs) {
        if (!paragraph) continue;

        const blockElements = [];

        if (paragraph.h2 && typeof paragraph.index === "number") {
          blockElements.push({
            type: "h2",
            index: paragraph.index,
            text: paragraph.h2,
          });
        }

        if (Array.isArray(paragraph.text)) {
          for (const t of paragraph.text) {
            if (t && typeof t.index === "number") {
              blockElements.push({
                type: "p",
                index: t.index,
                text: t.text ?? "",
                len: (t.text ?? "").length
              });
            }
          }
        }
        if (Array.isArray(paragraph.images)) {
          for (const img of paragraph.images) {
            if (img && typeof img.index === "number") {
              blockElements.push({
                type: "image",
                index: img.index,
                title: img.title ?? null,
                alt: img.alt ?? null,
                caption: img.caption ?? null,
              });
            }
          }
        }

        if (Array.isArray(paragraph.tables)) {
          for (const table of paragraph.tables) {
            if (table && typeof table.index === "number") {
              blockElements.push({
                type: "table",
                index: table.index,
                rows: Array.isArray(table.rows) ? table.rows : [],
              });
            }
          }
        }

        const rowsList = paragraph.rows_list;
        if (rowsList) {
          // Вариант A: rows_list — объект { text: [...], index }
          if (typeof rowsList === "object" && !Array.isArray(rowsList) && typeof rowsList.index === "number") {
            blockElements.push({
              type: "rows",
              index: rowsList.index,
              text: Array.isArray(rowsList.text) ? rowsList.text : [],
            });
          }
          // Вариант B: rows_list — массив объектов Rows
          else if (Array.isArray(rowsList)) {
            for (const r of rowsList) {
              if (r && typeof r.index === "number") {
                blockElements.push({
                  type: "rows",
                  index: r.index,
                  text: Array.isArray(r.text) ? r.text : [],
                });
              }
            }
          }
        }

        blockElements.sort((a, b) => a.index - b.index);

        resultBlocks.push({
          h2: paragraph.h2 ?? null,
          index: paragraph.index ?? null,
          links: Array.isArray(paragraph.links) ? paragraph.links : [],
          elements: blockElements,
        });
      }

      return resultBlocks;
    },

    // CSS-класс по проценту совпадения
    matchClass(percent) {
      if (percent >= 90) return 'match-high'
      if (percent >= 60) return 'match-mid'
      return 'match-low'
    }
  }
}
</script>

<style scoped lang="scss">
.compare-stats {
  padding: 20px;
  font-family: inherit;
  color: #222;
}

/* ===== Header ===== */
.stats-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef0f2;

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #6c757d;
    }
  }

  .totals {
    display: flex;
    gap: 12px;
  }

  .total-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 16px;
    background: #f8f9fa;
    border: 1px solid #eef0f2;
    border-radius: 8px;
    min-width: 100px;

    .label {
      font-size: 0.7rem;
      color: #868e96;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 2px;
    }

    .value {
      font-size: 1.1rem;
      font-weight: 600;
      color: #212529;

      &.match-high {
        color: #2b8a3e;
      }

      &.match-mid {
        color: #b8860b;
      }

      &.match-low {
        color: #c92a2a;
      }
    }
  }
}

/* ===== Summary table ===== */
.summary-table {
  margin-bottom: 28px;
  border: 1px solid #eef0f2;
  border-radius: 8px;
  overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  thead {
    background: #f1f3f5;

    th {
      padding: 10px 14px;
      text-align: left;
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #495057;
      border-bottom: 1px solid #e9ecef;

      &:not(:first-child):not(:nth-child(2)) {
        text-align: center;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f1f3f5;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #f8f9fa;
      }
    }

    td {
      padding: 10px 14px;
      color: #343a40;

      &.num {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      &.label {
        font-weight: 500;
      }

      &.delta-zero {
        color: #868e96;
      }

      &.delta-nonzero {
        color: #b8860b;
      }

      &.match {
        text-align: center;
      }
    }
  }
}

/* ===== Badges ===== */
.badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  &.match-high {
    background: #e6f7ec;
    color: #2b8a3e;
  }

  &.match-mid {
    background: #fff8e1;
    color: #b8860b;
  }

  &.match-low {
    background: #ffe8e8;
    color: #c92a2a;
  }
}

/* ===== Paragraphs ===== */
.paragraphs {
  h4 {
    margin: 0 0 14px;
    font-size: 1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #6c757d;
    }
  }
}

.paragraph-card {
  margin-bottom: 14px;
  border: 1px solid #eef0f2;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;

  .paragraph-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #f8f9fa;
    border-bottom: 1px solid #eef0f2;

    .col {
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }

    .index {
      font-size: 0.7rem;
      font-weight: 600;
      color: #868e96;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex-shrink: 0;
    }

    .h2 {
      flex: 1;
      font-size: 0.9rem;
      font-weight: 500;
      color: #212529;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .column-header {
    padding: 16px 20px;
    background: #fafafa;
    border-bottom: 1px solid #e5e5e5;
    font-weight: 600;
    font-size: 0.9rem;
    color: #333;
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;

    .header-text {
      h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: #343a40;
      }

      small {
        color: #6c757d;
        font-size: 0.8rem;
      }
    }

    .header-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.3rem;
      background: rgba(255, 255, 255, 0.7);
      color: #495057;
    }

    &.placement-header .header-icon {
      color: #1976d2;
    }

    &.source-header .header-icon {
      color: #7b1fa2;
    }
  }

  .paragraph-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;

    .col {
      padding: 12px 14px;

      &:first-child {
        border-right: 1px solid #f1f3f5;
      }


    }

    .col-title {
      font-size: 0.7rem;
      font-weight: 600;
      color: #868e96;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 8px;
    }


  }

  .paragraph-foot {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 14px;
    background: #fafbfc;
    border-top: 1px solid #f1f3f5;

    .chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 9px;
      background: #fff;
      border: 1px solid #eef0f2;
      border-radius: 10px;
      font-size: 0.72rem;
      color: #495057;
      font-variant-numeric: tabular-nums;

      i {
        color: #adb5bd;
        font-size: 0.8rem;
      }
    }
  }
}

/* ===== Empty ===== */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #adb5bd;
  text-align: center;

  i {
    font-size: 2.5rem;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}
</style>